import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface RacingFilters {
  analysisTypeId: number;
  trackType?: string;
  state?: string;
  trackCode?: string;
  trackCondition?: string;
  startDate?: string;
  endDate?: string;
}

export interface FilterOptions {
  trackTypes: string[];
  states: string[];
  tracks: { track_code: string; track_name: string; state: string }[];
}

// Rows carry many numeric columns (pg returns numerics as strings); typed loosely.
export type RacingRow = Record<string, string | number | null>;

function toQuery(f: RacingFilters): string {
  const params = new URLSearchParams({ analysisTypeId: String(f.analysisTypeId) });
  (['trackType', 'state', 'trackCode', 'trackCondition', 'startDate', 'endDate'] as const).forEach((k) => {
    if (f[k]) params.set(k, f[k] as string);
  });
  return params.toString();
}

export function useRacingFilterOptions() {
  return useQuery({
    queryKey: ['racing-analytics', 'filter-options'],
    queryFn: () => apiClient<FilterOptions>('/racing-analytics/filter-options'),
  });
}

function useRacingTab(tab: string, filters: RacingFilters) {
  return useQuery({
    queryKey: ['racing-analytics', tab, filters],
    queryFn: () => apiClient<RacingRow[]>(`/racing-analytics/${tab}?${toQuery(filters)}`),
  });
}

export const useMeetingPerformance = (f: RacingFilters) => useRacingTab('meeting-performance', f);
export const useTrackPerformance = (f: RacingFilters) => useRacingTab('track-performance', f);
export const useExoticBets = (f: RacingFilters) => useRacingTab('exotic-bets', f);
export const useRoiAnalysis = (f: RacingFilters) => useRacingTab('roi-analysis', f);
export const useTrackCondition = (f: RacingFilters) => useRacingTab('track-condition', f);

// Slice 2: the dashboard tabs use only analysisTypeId.
export interface RacingDashboard {
  kpis: RacingRow | null;
  byTrackType: RacingRow[];
  byState: RacingRow[];
  byDayOfWeek: RacingRow[];
  monthlyTrend: RacingRow[];
  byRaceClass: RacingRow[];
}
export interface RoiDashboard {
  kpis: RacingRow | null;
  monthly: RacingRow[];
  byPriceBucket: RacingRow[];
}

export function useRacingDashboard(analysisTypeId: number) {
  return useQuery({
    queryKey: ['racing-analytics', 'dashboard', analysisTypeId],
    queryFn: () => apiClient<RacingDashboard>(`/racing-analytics/dashboard?analysisTypeId=${analysisTypeId}`),
  });
}
export function useRoiDashboard(analysisTypeId: number) {
  return useQuery({
    queryKey: ['racing-analytics', 'roi-dashboard', analysisTypeId],
    queryFn: () => apiClient<RoiDashboard>(`/racing-analytics/roi-dashboard?analysisTypeId=${analysisTypeId}`),
  });
}

// Slice 3: High-Probability ROI (a betting-strategy calculator; not filtered by
// analysisTypeId - it reads the pre-aggregated high_probability_roi_daily table).
export interface HpKpi {
  bets: number;
  wins: number;
  places: number;
  winStrike: number;
  placeStrike: number | null;
  avgOddsMarket: number;
  avgOddsSp: number;
  roiWinMarket: number;
  roiWinSp: number;
  roiPlaceSp: number | null;
  roiEwMarket: number | null;
  roiEwSp: number | null;
  staked: number;
  profitMarket: number;
  profitSp: number;
}
export interface HpDailyRow {
  date: string;
  combined_cum_market: number;
  combined_cum_sp: number;
  [key: string]: string | number;
}
export interface HpResponse {
  unitValue: number;
  units: { top: { win: number; place: number }; value: { win: number; place: number } };
  days: number[];
  placeDataStartDate: string;
  placeAvailable: boolean;
  kpis: { top: HpKpi; value: HpKpi; combined: HpKpi };
  daily: HpDailyRow[];
  spansValueRuleChange: boolean;
  valueRuleChangeDate: string;
}

export interface HpParams {
  startDate?: string;
  endDate?: string;
  unitValue: number;
  topWin: number;
  topPlace: number;
  valueWin: number;
  valuePlace: number;
  days: number[];
}

export function useHighProbabilityRoi(p: HpParams) {
  const params = new URLSearchParams({
    unitValue: String(p.unitValue),
    topWin: String(p.topWin),
    topPlace: String(p.topPlace),
    valueWin: String(p.valueWin),
    valuePlace: String(p.valuePlace),
    days: p.days.join(','),
  });
  if (p.startDate) params.set('startDate', p.startDate);
  if (p.endDate) params.set('endDate', p.endDate);
  return useQuery({
    queryKey: ['racing-analytics', 'high-probability-roi', p],
    queryFn: () => apiClient<HpResponse>(`/racing-analytics/high-probability-roi?${params.toString()}`),
  });
}
